import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import platformApiService from '../../services/platformApi';
import { ArrowLeftIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/30',
  trial: 'bg-sky-500/10 text-sky-300 ring-1 ring-sky-500/30',
  suspended: 'bg-red-500/10 text-red-300 ring-1 ring-red-500/30',
  cancelled: 'bg-slate-500/10 text-slate-400 ring-1 ring-slate-500/30',
};

const fmtDate = (value: string | null | undefined) => (value ? new Date(value).toLocaleString() : '—');

const PlatformMunicipalityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-municipality', id],
    queryFn: async () => {
      const response = await platformApiService.getMunicipality(id as string);
      if (!response.success) throw new Error(response.error?.message || 'Failed to load municipality');
      return response.data;
    },
    enabled: !!id,
  });

  return (
    <div>
      <button
        onClick={() => navigate('/platform-ops')}
        className="mb-4 flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to municipalities
      </button>

      {isLoading ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-8 text-center text-sm text-slate-500">Loading…</div>
      ) : error ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-8 text-center text-sm text-red-300">{(error as Error).message}</div>
      ) : data ? (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-white">{data.name}</h1>
              <p className="text-sm text-slate-500">{data.code} · {data.email || 'no email on file'} · {data.phone || 'no phone on file'}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-medium capitalize ${STATUS_STYLES[data.status] || STATUS_STYLES.cancelled}`}>
              {data.status}{data.status === 'suspended' && data.suspendedReason ? ` — ${data.suspendedReason}` : ''}
            </span>
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
            <h2 className="mb-2 text-sm font-semibold text-white">Public status page</h2>
            <p className="mb-3 text-xs text-slate-500">
              Share this link with {data.name} so residents can view live service status without logging in.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-300">
                {window.location.origin}/service-status/{data.code}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/service-status/${data.code}`);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:border-emerald-500 hover:text-emerald-300"
              >
                {copied ? <CheckIcon className="h-3.5 w-3.5" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h2 className="mb-3 text-sm font-semibold text-white">Staff</h2>
              {data.staff.length === 0 ? (
                <p className="text-sm text-slate-500">No staff accounts.</p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-slate-500">
                      <th className="pb-2 font-medium">Name</th>
                      <th className="pb-2 font-medium">Role</th>
                      <th className="pb-2 font-medium">Last login</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.staff.map((s) => (
                      <tr key={s.id} className="border-t border-slate-800/60">
                        <td className="py-2 text-slate-200">
                          {s.first_name} {s.last_name}
                          <div className="text-xs text-slate-500">{s.email}</div>
                        </td>
                        <td className="py-2 capitalize text-slate-300">{s.role}</td>
                        <td className="py-2 text-slate-400">{fmtDate(s.last_login)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <h2 className="mb-3 text-sm font-semibold text-white">Reports by status</h2>
              {data.statusBreakdown.length === 0 ? (
                <p className="text-sm text-slate-500">No reports yet.</p>
              ) : (
                <div className="space-y-2">
                  {data.statusBreakdown.map((row) => (
                    <div key={row.status} className="flex items-center justify-between text-sm">
                      <span className="capitalize text-slate-300">{row.status.replace(/_/g, ' ')}</span>
                      <span className="font-medium text-white">{row.n}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
            <h2 className="mb-3 text-sm font-semibold text-white">Recent reports</h2>
            {data.recentReports.length === 0 ? (
              <p className="text-sm text-slate-500">No reports yet.</p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500">
                    <th className="pb-2 font-medium">Report</th>
                    <th className="pb-2 font-medium">Title</th>
                    <th className="pb-2 font-medium">Status</th>
                    <th className="pb-2 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentReports.map((r) => (
                    <tr key={r.id} className="border-t border-slate-800/60">
                      <td className="py-2 text-slate-300">{r.report_number}</td>
                      <td className="py-2 text-slate-200">{r.title}</td>
                      <td className="py-2 capitalize text-slate-300">{r.status.replace(/_/g, ' ')}</td>
                      <td className="py-2 text-slate-400">{fmtDate(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PlatformMunicipalityDetail;
